/**
 * Full-screening header right side (fullscreen-spec §3): the bordered score
 * box — big extrabold score with a "/100" gray baseline suffix over an ~8px
 * fully-rounded GREEN linear bar at score/100 (NOT the v1 ScoreGauge arc) —
 * plus the stacked outline chips. "Guarantor Recommended" follows v1's
 * hasGuarantorRecommendation rule (deriveGuarantorRecommended); "Rental
 * Guarantee" shows for any scored app.
 *
 * Both chips are click-popovers (spec §18 mock): "Rental Guarantee" → the
 * eligibility/tier card + "Buy X% Guarantee & Approve"; "Guarantor Recommended"
 * → "View Risk Factors" (scrolls to the Risk Flags card) + "Request Guarantor".
 * The action buttons render only when the page passes the matching handler
 * (gated by can.* + decided-status upstream), so the popovers degrade to
 * info-only where the action isn't allowed.
 *
 * The page renders this ONLY when screeningKind === 'full' — the kind gate
 * lives at the render site (converted-app invariant), never on data presence.
 */
import {
	Shield,
	ShieldAlert,
	ShieldCheck,
	ShieldX,
	TriangleAlert,
	Wallet,
	X,
} from 'lucide-react'
import { useState } from 'react'
import { GuarantorBandBar } from '#app/components/application-detail-v2/vendor/ScoreBandBar'
import { ScoreDisplay } from '#app/components/application-detail-v2/vendor/ScoreDisplay'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover'
import type { GuarantorRequestsView } from '#app/utils/application-detail-v2/guarantor-requests'
import {
	type HouseholdGuarantorChipState,
	deriveGuaranteeTier,
	deriveGuarantorAcceptability,
	deriveGuarantorRecommended,
	deriveGuarantorScore,
	deriveHeaderScore,
	deriveHouseholdGuarantorChip,
} from '#app/utils/application-detail-v2/report-display'
import { cn } from '#app/utils/misc'
import {
	type CancelInviteContext,
	GuarantorApplicantSection,
} from './GuarantorStatusList'

/** Guarantor-score risk tone → chip text colour (shared thresholds, spec §18). */
const GUARANTOR_TONE_TEXT = {
	low: 'text-[#16A34A]',
	medium: 'text-[#F59F0A]',
	high: 'text-[#DC2626]',
} as const

/** Scroll the Risk Flags card into view (the "View Risk Factors" link). */
function scrollToRiskFlags() {
	document
		.getElementById('risk-flags')
		?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/** White outline status chip (spec §3 right rail) — also the popover trigger,
 * so it's a real <button> (PopoverTrigger renders one). */
const CHIP_CLASS =
	'flex min-h-16 w-full cursor-pointer items-center justify-center gap-2.5 rounded-2xl border border-[#F0F0F0] bg-[#fbfdfd] px-4 py-3 text-center text-[15px] font-medium transition-colors hover:bg-[#F6F7F5] [&>svg]:size-5'

const POPOVER_CLASS =
	'w-[360px] rounded-3xl p-6 shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A] bg-[#fbfdfd]'

/** Guarantor chip inner: the recommendation label with a shield icon. The
 * accepted count is NOT shown here — it lives in the popover / household status
 * modal the chip opens (avoids duplicating it on the chip itself). The wrapping
 * chip sets the tone text colour, so the label/icon inherit it. */
/** The shield says WHICH verdict at a glance: check = every guarantor is
 * acceptable, cross = none are, alert = mixed or not yet judged. A pending
 * recommendation keeps the plain shield — nothing has been judged yet. */
const GUARANTOR_CHIP_ICON = {
	recommended: Shield,
	acceptable: ShieldCheck,
	unacceptable: ShieldX,
	mixed: ShieldAlert,
} as const satisfies Record<HouseholdGuarantorChipState, typeof Shield>

function GuarantorChipContent({
	label,
	state = 'recommended',
}: {
	label: string
	state?: HouseholdGuarantorChipState
}) {
	const Icon = GUARANTOR_CHIP_ICON[state]
	return (
		<span className="flex items-center justify-center gap-2.5">
			<Icon aria-hidden="true" className="size-5 shrink-0" />
			<span className="text-[15px] leading-snug font-medium">{label}</span>
		</span>
	)
}

/** Explainer card shown inside the chip popovers on the SIMPLE report depth
 * ("What Is A Guarantor?" / "What Is Rent Guarantee") — a bordered panel with
 * a medium title over muted body copy. */
function InfoCard({ title, body }: { title: string; body: string }) {
	return (
		<div className="mt-4 rounded-2xl border border-[#F0F0F0] p-4">
			<h4 className="text-[15px] font-medium text-[#32302F]">{title}</h4>
			<p className="mt-2 text-sm leading-relaxed text-[#747474]">{body}</p>
		</div>
	)
}

/** Top-right close (×) button shared by every chip popover. */
function PopoverClose({ onClose }: { onClose: () => void }) {
	return (
		<button
			type="button"
			onClick={onClose}
			aria-label="Close"
			className="-mt-1 -mr-1 shrink-0 cursor-pointer rounded-full p-1 text-[#747474] transition-colors hover:bg-[#F0F0F0] hover:text-[#32302F]"
		>
			<X className="size-4" />
		</button>
	)
}

/** Presentational score box + chips shared with the combined header. */
export function ScorePanelView({
	score,
	guarantorRecommended,
	householdGuarantorVerdicts,
	guarantorScore = null,
	guarantorRequests,
	onApprove,
	onRequestGuarantor,
	onGuarantorStatusClick,
	cancelInvite,
	simple = false,
	scoreDisplay = 'band',
}: {
	score: number
	guarantorRecommended: boolean
	/** Combined page only: one entry per guarantor ON the household (true
	 * acceptable / false not / null not judged). Given these, the chip reports
	 * the household's guarantors instead of a recommendation. */
	householdGuarantorVerdicts?: ReadonlyArray<boolean | null>
	/** Guarantor-recommendation score (0–100) → chip COLOUR (spec §18). */
	guarantorScore?: number | null
	/** This applicant's guarantor request(s). When `requested`, the chip flips
	 * from "recommend" to a status view; null/absent → recommend-only. */
	guarantorRequests?: GuarantorRequestsView
	/** "Buy X% Guarantee & Approve" — omitted (button hidden) when approving
	 * isn't allowed for the current status/permission. */
	onApprove?: () => void
	/** "Request Guarantor" — omitted (button hidden) when not allowed. */
	onRequestGuarantor?: () => void
	/** Combined surface: when set AND a guarantor is requested, the chip becomes
	 * a button that opens the household status MODAL instead of the inline
	 * popover (the individual surface omits this → inline popover). */
	onGuarantorStatusClick?: () => void
	/** TT-42: enables "Cancel invite" on live guarantor-invite rows in the
	 * popover. Gated upstream (can.cancelInvitation + decision open). */
	cancelInvite?: CancelInviteContext
	/** Simple report depth (PropertyStrip's Simple|Enterprise toggle). When set,
	 * the chip popovers gain the explainer cards ("What Is A Guarantor?" /
	 * "What Is Rent Guarantee"); enterprise/combined omit them. */
	simple?: boolean
	/** Per-landlord score_display setting (DTO): band bar (default) or NN/100. */
	scoreDisplay?: 'band' | 'numeric'
}) {
	const [open, setOpen] = useState<'guarantee' | 'guarantor' | null>(null)
	// showGuarantee + tier feed the commented-out Rental Guarantee chip below.
	const showGuarantee = score > 0
	const tier = deriveGuaranteeTier(score)
	void showGuarantee
	void tier
	// The chip shows once a guarantor is either recommended OR already requested
	// (a manual request with score 0 must still surface its status). Colour
	// follows the guarantor score (shared risk thresholds).
	const requested = guarantorRequests?.requested ?? false
	// Once guarantors have JOINED the household (combined page passes their
	// verdicts) the chip reports on them — green only when every one of them is
	// acceptable. With none joined it falls back to the recommendation, which is
	// never green (client 2026-08-27). The individual page passes no verdicts,
	// so it keeps the recommendation behaviour.
	const chip = deriveHouseholdGuarantorChip({
		verdicts: householdGuarantorVerdicts ?? [],
		// Either reason surfaces the chip; the score is left honest so the tone
		// still reflects the real recommendation strength.
		requested: requested || guarantorRecommended,
		guarantorScore,
	})
	const showGuarantorChip = chip != null
	const guarantorToneText = GUARANTOR_TONE_TEXT[chip?.tone ?? 'medium']
	// Fixed label — the API's guarantorRecommendation text is deliberately NOT
	// shown (it varies, e.g. "Guarantor Required"); the accepted count lives in
	// the popover / household status modal it opens.
	const guarantorChipLabel = chip?.label ?? 'Guarantor Recommended'
	const guarantorChipState = chip?.state ?? 'recommended'
	// Individual surface = a single requesting applicant.
	const applicant = guarantorRequests?.perApplicant[0]

	return (
		// Chip column LEFT, score RIGHT (client 2026-08-27 — the chips used to
		// stack BELOW the score). Order inside the column is Rental Guarantee on
		// top, Guarantor Recommended under it; with the guarantee chip currently
		// commented off, only the guarantor card shows. When neither condition
		// fires the column drops out entirely and the score sits centered on its
		// own, exactly as before. Shared with the combined header (CombinedHeader
		// renders this same view), so both pages move together.
		<div className="flex min-w-0 flex-1 basis-130 flex-row items-center justify-center gap-4 lg:basis-105 xl:min-w-0">
			{/* Only mount the chip column when a guarantor chip actually renders.
			    Gating on `showGuarantee` alone (Rental Guarantee is commented off)
			    left an empty 220px column — High Risk (chip present) looked fine,
			    Strong Applicant (no chip, green bar only) shoved the score past
			    the header card edge. */}
			{showGuarantorChip ? (
				<div className="flex w-[42%] max-w-[220px] min-w-[140px] shrink-0 flex-col gap-3">
					{requested && onGuarantorStatusClick ? (
						// Combined surface: chip opens the household status modal.
						<button
							type="button"
							onClick={onGuarantorStatusClick}
							className={cn(CHIP_CLASS, guarantorToneText)}
						>
							<GuarantorChipContent
								label={guarantorChipLabel}
								state={guarantorChipState}
							/>
						</button>
					) : (
						<Popover
							open={open === 'guarantor'}
							onOpenChange={(o) => setOpen(o ? 'guarantor' : null)}
						>
							<PopoverTrigger className={cn(CHIP_CLASS, guarantorToneText)}>
								<GuarantorChipContent
									label={guarantorChipLabel}
									state={guarantorChipState}
								/>
							</PopoverTrigger>
							<PopoverContent
								align="end"
								// Always open DOWNWARD from the chip. Radix flips to `top`
								// by default when the trigger sits low in the viewport
								// (avoidCollisions) — here that dropped the panel UP over
								// the score card. Pin it below and disable the flip.
								side="bottom"
								avoidCollisions={false}
								className={POPOVER_CLASS}
								// "View Risk Factors" scrolls down to #risk-flags; without this,
								// Radix restores focus to the trigger chip on close and the
								// browser snaps the page back up to the sticky header.
								onCloseAutoFocus={(event) => event.preventDefault()}
							>
								{requested && applicant ? (
									<>
										<div className="flex items-start justify-between gap-2">
											<div>
												<h3 className="text-sm text-[#747474]">
													Guarantor Status
												</h3>
												<p className="text-lg font-semibold text-[#32302F]">
													{applicant.accepted} of {applicant.total} Accepted
												</p>
											</div>
											<PopoverClose onClose={() => setOpen(null)} />
										</div>
										<div className="mt-4 space-y-2">
											<GuarantorApplicantSection
												applicant={applicant}
												cancelInvite={cancelInvite}
											/>
										</div>
										{onRequestGuarantor ? (
											<button
												type="button"
												onClick={() => {
													setOpen(null)
													onRequestGuarantor()
												}}
												className="mt-4 h-12 w-full cursor-pointer rounded-full bg-[#32302F] text-sm font-medium text-white transition-colors"
											>
												Request Another Guarantor
											</button>
										) : null}
									</>
								) : (
									<>
										<div className="flex items-start justify-between gap-2">
											<div className="flex items-start gap-2">
												<Shield
													aria-hidden="true"
													className={cn(
														'mt-0.5 size-5 shrink-0',
														guarantorToneText,
													)}
												/>
												<h3 className="text-lg font-semibold text-[#32302F]">
													Guarantor Recommended
												</h3>
											</div>
											<PopoverClose onClose={() => setOpen(null)} />
										</div>
										<p className="mt-2 text-sm text-[#747474] capitalize">
											This tenant's profile shows risk factors that make a
											guarantor advisable
										</p>
										<button
											type="button"
											onClick={() => {
												setOpen(null)
												scrollToRiskFlags()
											}}
											className="mt-4 cursor-pointer text-sm font-semibold text-[#32302F] underline underline-offset-4"
										>
											View Risk Factors
										</button>
										{simple ? (
											<InfoCard
												title="What Is A Guarantor?"
												body="A guarantor co-signs the lease and is legally responsible for rent and damages if the tenant can't pay. Added protection for you."
											/>
										) : null}
										{onRequestGuarantor ? (
											<button
												type="button"
												onClick={() => {
													setOpen(null)
													onRequestGuarantor()
												}}
												className="mt-4 h-12 w-full cursor-pointer rounded-full bg-[#32302F] text-sm font-medium text-white transition-colors"
											>
												Request Guarantor
											</button>
										) : null}
									</>
								)}
							</PopoverContent>
						</Popover>
					)}
					{/* Rental Guarantee chip + popover — commented off for now (don't
					    show it). Restore this block to bring it back, and move it ABOVE
					    the guarantor chip: the column reads Rental Guarantee on top,
					    Guarantor Recommended under it (client 2026-08-27). */}
					{/* {showGuarantee ? (
						<Popover
							open={open === 'guarantee'}
							onOpenChange={(o) => setOpen(o ? 'guarantee' : null)}
						>
							<PopoverTrigger className={cn(CHIP_CLASS, 'text-[#16A34A]')}>
								<Shield aria-hidden="true" />
								Rental Guarantee
							</PopoverTrigger>
							<PopoverContent align="end" className={POPOVER_CLASS}>
								<div className="flex items-start justify-between gap-2">
									<div className="flex items-start gap-2">
										<Shield
											aria-hidden="true"
											className="mt-0.5 size-5 shrink-0 text-[#16A34A]"
										/>
										<h3 className="text-lg font-semibold text-[#32302F]">
											Rental Guarantee Eligibility
										</h3>
									</div>
									<PopoverClose onClose={() => setOpen(null)} />
								</div>
								<p className="mt-2 text-sm text-[#747474] capitalize">
									Based on a score of {score}/100, this tenant qualifies for:
								</p>
								<div className="mt-4 rounded-2xl border border-[#F0F0F0] p-4">
									<div className="flex items-baseline justify-between">
										<span className="text-xl font-bold text-[#32302F]">
											{tier.percent}%
										</span>
										<span className="font-regular text-base text-[#32302F]">
											{tier.label}
										</span>
									</div>
									<p className="mt-3 text-sm text-[#747474]">
										{tier.description}
									</p>
								</div>
								{simple ? (
									<InfoCard
										title="What Is Rent Guarantee"
										body="Protect your rental income even if your tenant stops paying. Rent Guarantee covers unpaid rent so a default doesn't become your loss, giving you peace of mind on every approval."
									/>
								) : null}
								{onApprove ? (
									<button
										type="button"
										onClick={() => {
											setOpen(null)
											onApprove()
										}}
										disabled={true} // TODO: enable when the action is allowed (gated by can.approve upstream)
										className="mt-4 h-12 w-full cursor-pointer rounded-full bg-[#32302F] text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:bg-[#747474]"
										// className="mt-4 h-12 w-full cursor-pointer rounded-full bg-[#32302F] text-sm font-medium text-white transition-colors"
									>
										Buy {tier.percent}% Guarantee & Approve
									</button>
								) : null}
							</PopoverContent>
						</Popover>
					) : null} */}
				</div>
			) : null}
			<ScoreDisplay
				score={score}
				display={scoreDisplay}
				size="lg"
				// Report header only (client mock 2026-07-22): numeric mode shows
				// NN/100 with a band-tinted gauge under it.
				withBar
				className="max-w-full"
			/>
		</div>
	)
}

/**
 * Header right-side placeholder shown for a full-screening app whose report
 * isn't generated yet (TT2-502): the score box would otherwise read a confusing
 * "0/100" before the applicant submits / the report completes. Mirrors
 * `AwaitingReportSection`'s wording; the body still renders the full
 * awaiting/processing/failed section below.
 */
export function ScorePendingPanel({
	failed,
	held = false,
	applicantName,
}: {
	failed: boolean
	/** TT-12: submitted but the report is held on low credits — distinct from
	 * "screening still in progress". Takes precedence over the default copy. */
	held?: boolean
	/** Names the applicant in the not-generated / failed copy (client
	 * 2026-09-07 — the same message on pre-screen and full screening). */
	applicantName?: string
}) {
	// Same anatomy as the combined header's "Combined Report Unavailable" box
	// (client 2026-09-07): icon + uppercase title + one status line. The
	// failed state used to be a full-width body card ("We hit an error…");
	// it lives here now, and the kebab's Generate Report is the action.
	const who = applicantName?.trim() || 'The applicant'
	const title = held
		? 'Report On Hold'
		: failed
			? 'Screening Report Unavailable'
			: 'Screening Report Not Generated Yet'
	const body = held
		? 'Your credit balance was too low to generate the report. Add credits, then generate it below.'
		: failed
			? `We hit an error generating ${who}'s report. Nothing more is needed from ${who} — use Generate Report in the actions menu to try again.`
			: `${who} hasn't completed their screening yet. Their report — scores, verification, and risk insights — will appear here once it's generated.`
	return (
		<div className="flex w-full max-w-140 flex-col items-center gap-2 rounded-2xl border border-[#F0F0F0] bg-[#FBFDFD] px-8 py-6 text-center text-sm text-[#747474]">
			<span className="flex items-center gap-2.5 text-[15px] font-semibold text-[#32302F] uppercase">
				{held ? (
					<Wallet
						aria-hidden="true"
						className="size-6 shrink-0 text-[#F59F0A]"
					/>
				) : failed ? (
					<TriangleAlert
						aria-hidden="true"
						className="size-6 shrink-0 text-[#DC2626]"
					/>
				) : (
					<img
						src="/img/fluent_clock-warning-28-regular.svg"
						alt=""
						className="size-6 shrink-0"
					/>
				)}
				{title}
			</span>
			<span>{body}</span>
		</div>
	)
}

/**
 * Header right-side panel for a GUARANTOR's own report (TT2-511): a guarantor is
 * judged on ACCEPTABILITY, not a 0–100 score, so we show the
 * `predictions.guarantorAccessibility` verdict (Acceptable / Not Acceptable)
 * where an applicant shows the score box. Rendered as the two-band pill row
 * (client 2026-08-27) so it reads like the applicant's score band; the
 * section title is dropped because the verdict word already says it.
 */
export function GuarantorAcceptabilityPanel({
	acceptable,
	hasVerdict,
}: {
	acceptable: boolean
	hasVerdict: boolean
}) {
	return (
		<div className="flex min-w-0 flex-1 basis-140 flex-wrap items-center justify-end gap-4">
			{hasVerdict ? (
				// The bar alone — no tile, no verdict wording (client 2026-08-27);
				// the saturated band carries the verdict and screen readers hear it
				// through the bar's aria-label.
				// Intrinsic width from the two 132px segments (not `xl:w-90`):
				// the old fixed rail left empty space to the right of Acceptable.
				<GuarantorBandBar
					acceptable={acceptable}
					size="lg"
					className="max-w-full"
				/>
			) : (
				// No verdict ⇒ say so. An all-faded bar is two near-white tints and
				// reads as an empty header rather than "not assessed".
				<span className="text-base font-medium text-[#747474]">
					Not Available
				</span>
			)}
		</div>
	)
}

export function HeaderScorePanel({
	aiReportData,
	applicationScore,
	applicationType,
	guarantorRequests,
	onApprove,
	onRequestGuarantor,
	cancelInvite,
	simple = false,
	scoreDisplay = 'band',
}: {
	aiReportData: Record<string, unknown> | null
	/** application.score — overallScore ?? screeningScore (compose). */
	applicationScore: number | null
	applicationType: 'primary' | 'co_applicant' | 'guarantor'
	/** This applicant's guarantor request(s) (DTO). Flips the chip to a status
	 * view once a guarantor is requested. */
	guarantorRequests?: GuarantorRequestsView
	onApprove?: () => void
	onRequestGuarantor?: () => void
	/** TT-42: enables "Cancel invite" on live guarantor-invite rows. */
	cancelInvite?: CancelInviteContext
	/** Simple report depth → the chip popovers show the explainer cards. */
	simple?: boolean
	/** Per-landlord score_display setting (DTO): band bar (default) or NN/100. */
	scoreDisplay?: 'band' | 'numeric'
}) {
	// A guarantor's own report shows an acceptability verdict, not a score.
	if (applicationType === 'guarantor') {
		const { acceptable, hasVerdict } =
			deriveGuarantorAcceptability(aiReportData)
		return (
			<GuarantorAcceptabilityPanel
				acceptable={acceptable}
				hasVerdict={hasVerdict}
			/>
		)
	}
	return (
		<ScorePanelView
			score={deriveHeaderScore(aiReportData, applicationScore)}
			guarantorRecommended={deriveGuarantorRecommended(
				aiReportData,
				applicationType,
			)}
			guarantorScore={deriveGuarantorScore(aiReportData, applicationType)}
			guarantorRequests={guarantorRequests}
			onApprove={onApprove}
			onRequestGuarantor={onRequestGuarantor}
			cancelInvite={cancelInvite}
			simple={simple}
			scoreDisplay={scoreDisplay}
		/>
	)
}
